import type { AdmissionApplication, GuardianRelation } from '../../../types/admissions';
import { isSystemFieldKey } from './admissionFormFields';

/**
 * Resolves the answer to any form-template field for one application, from
 * wherever the backend stored it on submission:
 *
 *   - applicant + admission fields → top-level on the application
 *   - father* / mother* fields     → Guardian records once the family is
 *                                    resolved, `pendingGuardians` before that
 *   - custom fields                → `formData`
 *
 * Returns the raw stored value (option value, ISO date, digits-only CNIC) so
 * the printed field renders it exactly as the blank form would have been
 * filled in by hand.
 */

export interface GuardianLike {
  relation: GuardianRelation | string;
  name?: string;
  cnic?: string;
  phone?: string;
  email?: string;
  occupation?: string;
}

export interface ApplicationValueSources {
  app: AdmissionApplication;
  /** Resolved Guardian records of the applicant's family. */
  guardians?: GuardianLike[];
}

const GUARDIAN_FIELD = /^(father|mother)(Name|Phone|Cnic|Email|Occupation)$/;

function guardianValue(src: ApplicationValueSources, relation: string, prop: string): unknown {
  const key = prop.toLowerCase() as keyof GuardianLike;
  const resolved = src.guardians?.find((g) => g.relation === relation);
  const pending = src.app.pendingGuardians?.find((g) => g.relation === relation);
  return resolved?.[key] || pending?.[key as keyof typeof pending] || src.app.formData?.[`${relation}${prop}`];
}

export function applicationFieldValue(key: string, src: ApplicationValueSources): unknown {
  const { app } = src;
  if (key === 'classId') return typeof app.classId === 'object' ? app.classId?._id : app.classId;

  const guardian = GUARDIAN_FIELD.exec(key);
  if (guardian) {
    // fatherName / fatherPhone are also stored top-level on the application.
    const topLevel = (app as unknown as Record<string, unknown>)[key];
    return topLevel || guardianValue(src, guardian[1], guardian[2]);
  }

  if (isSystemFieldKey(key)) return (app as unknown as Record<string, unknown>)[key];
  return app.formData?.[key];
}
